/**
 * Wave C geometry → Strategy RD concept drawings (BE fields only).
 */

import type { StrategyRdSessionOption } from "../types/strategyRd";
import { strategyRdConceptChartLabel } from "./strategyRdConceptCopy";
import {
  parseStrategyRdWaveCLocation,
  strategyRdChannelPriceAt,
  strategyRdKillzoneWindowUtc,
  strategyRdWaveCHasBands,
  strategyRdWaveCHasCandleSpan,
  strategyRdWaveCHasChannel,
  strategyRdWaveCHasCup,
  strategyRdWaveCHasDivergenceSwings,
  strategyRdWaveCHasFlagGeometry,
  strategyRdWaveCHasNecklinePattern,
  strategyRdWaveCHasWedgeLines,
  strategyRdWaveCIsBandId,
  strategyRdWaveCIsCandlePatternId,
  strategyRdWaveCIsChartPatternId,
  strategyRdWaveCIsCupId,
  strategyRdWaveCIsDivergenceChartDraw,
  strategyRdWaveCIsDivergenceId,
  strategyRdWaveCIsDonchianChartDraw,
  strategyRdWaveCIsDonchianId,
  strategyRdWaveCIsFlagId,
  strategyRdWaveCIsKillzoneId,
  strategyRdWaveCIsMacdCrossZeroChartDraw,
  strategyRdWaveCIsMacdCrossZeroId,
  strategyRdWaveCIsOscillatorId,
  strategyRdWaveCIsPennantMeta,
  strategyRdWaveCIsRectangleMeta,
  strategyRdWaveCIsRsId,
  strategyRdWaveCIsTriangleMeta,
  strategyRdWaveCIsVolumeId,
  strategyRdWaveCIsWedgeId,
  strategyRdWaveCNecklineSegment,
  strategyRdWaveCResolveDivPivotTimes,
  strategyRdOscSeriesPaneId,
  strategyRdResolveOscSeries,
  strategyRdWaveCTrianglePatternLabel,
  strategyRdWaveCVolumeLabel,
  strategyRdWaveCVolumePanePrice,
  strategyRdTrendLinePriceAt,
  type StrategyRdWaveCLocation,
  type StrategyRdWaveCTrendLine,
} from "./strategyRdWaveCLocation";

/** Compatible with StrategyRdConceptDrawing (avoids circular import). */
export type StrategyRdWaveCDrawing = {
  id: string;
  label: string;
  kind: "bar" | "zone" | "level" | "marker" | "ray";
  barTime: string | null;
  fromBarTime?: string | null;
  toBarTime?: string | null;
  high: number | null;
  low: number | null;
  price: number | null;
  fromPrice?: number | null;
  toPrice?: number | null;
  /** Ray/segment: stop at endpoints (`none`) or continue past `to` (`right`, default for channels). */
  lineExtend?: "none" | "right";
  /** Price-swing arrow tip (Alchemy divergence `price_line`). */
  arrowRight?: boolean;
  showLabel?: boolean;
  sourceCandleType?: string | null;
  paneId?: string | null;
};
type OhlcLike = {
  timestamp?: string | null;
  time?: string | null;
  high: number | null;
  low: number | null;
};

function finiteNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function candleTime(c: OhlcLike): string | null {
  const t = (c.timestamp || c.time || "").trim();
  return t || null;
}

function rangeInWindow(
  candles: readonly OhlcLike[] | null | undefined,
  fromIso: string,
  toIso: string
): { high: number; low: number } | null {
  const fromMs = Date.parse(fromIso.replace(/\+00:00$/, "Z"));
  const toMs = Date.parse(toIso.replace(/\+00:00$/, "Z"));
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  let high = Number.NEGATIVE_INFINITY;
  let low = Number.POSITIVE_INFINITY;
  for (const c of candles ?? []) {
    const t = candleTime(c);
    if (!t) continue;
    const ms = Date.parse(t.replace(/\+00:00$/, "Z"));
    if (!Number.isFinite(ms) || ms < fromMs || ms > toMs) continue;
    const h = finiteNum(c.high);
    const l = finiteNum(c.low);
    if (h != null) high = Math.max(high, h);
    if (l != null) low = Math.min(low, l);
  }
  if (!Number.isFinite(high) || !Number.isFinite(low) || high <= low) return null;
  return { high, low };
}

function formationSpan(loc: StrategyRdWaveCLocation): {
  from: string | null;
  to: string | null;
} {
  const from =
    loc.patternStartTime ??
    loc.spanStartTime ??
    loc.flagStartTime ??
    loc.poleStartTime ??
    loc.pivots[0]?.barTime ??
    null;
  const to =
    loc.patternEndTime ??
    loc.spanEndTime ??
    loc.flagEndTime ??
    loc.barTime ??
    loc.pivots[loc.pivots.length - 1]?.barTime ??
    null;
  return { from, to };
}

/** Segment from line.p1 → through line.p2, ending at least at `throughTime` (Enter). */
function trendLineThrough(
  line: StrategyRdWaveCTrendLine,
  throughTime: string | null,
  throughBarIndex: number | null
): {
  fromBarTime: string;
  toBarTime: string;
  fromPrice: number;
  toPrice: number;
} {
  const fromBarTime = line.p1.barTime;
  const fromPrice = line.p1.price;
  const p2Time = line.p2.barTime;
  const endTime = throughTime && throughTime !== fromBarTime ? throughTime : p2Time;
  const useThrough =
    Boolean(throughTime) &&
    throughTime !== p2Time &&
    throughTime !== fromBarTime &&
    throughBarIndex != null;
  const toPrice = useThrough
    ? strategyRdTrendLinePriceAt(line, throughBarIndex) ?? line.p2.price
    : line.p2.price;
  const toBarTime = useThrough && throughTime ? throughTime : endTime === fromBarTime ? p2Time : endTime;
  return { fromBarTime, toBarTime, fromPrice, toPrice };
}

function flatLinePrice(line: StrategyRdWaveCTrendLine | null): number | null {
  if (!line) return null;
  // Prefer the channel value at the right edge (signal side).
  if (Number.isFinite(line.p2.price)) return line.p2.price;
  if (Number.isFinite(line.p1.price)) return line.p1.price;
  return null;
}

function asMetaRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asMetaTime(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  return null;
}

function pushDonchianChannel(
  out: StrategyRdWaveCDrawing[],
  opts: {
    prefix: string;
    label: string;
    loc: StrategyRdWaveCLocation;
    meta: Record<string, unknown>;
  }
): boolean {
  const { prefix, label, loc, meta } = opts;
  const from =
    loc.channelStartTime ??
    loc.upperLine?.p1.barTime ??
    loc.lowerLine?.p1.barTime ??
    loc.spanStartTime ??
    loc.patternStartTime;
  const to =
    loc.channelEndTime ??
    loc.upperLine?.p2.barTime ??
    loc.lowerLine?.p2.barTime ??
    loc.spanEndTime ??
    loc.patternEndTime ??
    loc.barTime;
  const upper =
    flatLinePrice(loc.upperLine) ?? loc.donchianHigh ?? loc.upper ?? null;
  const lower =
    flatLinePrice(loc.lowerLine) ?? loc.donchianLow ?? loc.lower ?? null;
  if (!from || !to || upper == null || lower == null || !(upper > lower)) {
    return false;
  }
  const mid = loc.mid != null && loc.mid > lower && loc.mid < upper
    ? loc.mid
    : (upper + lower) / 2;

  // Flat channel rails over the prior-length window — not a vertical Enter-bar box.
  // Labels sit on the first bar (from) via emit's fromTimeMs pointer.
  out.push({
    id: `${prefix}:donch-upper`,
    label: "Donch high",
    kind: "level",
    barTime: from,
    fromBarTime: from,
    toBarTime: to,
    high: null,
    low: null,
    price: upper,
    showLabel: true,
  });
  out.push({
    id: `${prefix}:donch-mid`,
    label: "Donch mid",
    kind: "level",
    barTime: from,
    fromBarTime: from,
    toBarTime: to,
    high: null,
    low: null,
    price: mid,
    showLabel: true,
  });
  out.push({
    id: `${prefix}:donch-lower`,
    label: "Donch low",
    kind: "level",
    barTime: from,
    fromBarTime: from,
    toBarTime: to,
    high: null,
    low: null,
    price: lower,
    showLabel: true,
  });
  out.push({
    id: `${prefix}:donch-channel`,
    label: label || "Donch",
    kind: "zone",
    barTime: from,
    fromBarTime: from,
    toBarTime: to,
    high: upper,
    low: lower,
    price: mid,
    // High/mid/low rails carry the labels at the first bar — skip a fourth chip.
    showLabel: false,
  });

  // Do not draw a separate Break rail — it usually equals Donch high/low and paints
  // pink over the teal/slate channel edge ("high turns red when broken").
  // The Break marker pill below is enough.

  // Frozen upper/lower rails already tell the channel story. Stepped donchian_path
  // is noisy (and each segment used to cycle the concept palette). Skip it.

  const markersRaw = meta.markers;
  if (Array.isArray(markersRaw)) {
    for (let i = 0; i < markersRaw.length; i += 1) {
      const row = asMetaRecord(markersRaw[i]);
      if (!row) continue;
      const barTime =
        asMetaTime(row.bar_time) ?? asMetaTime(row.time) ?? asMetaTime(row.timestamp);
      if (!barTime) continue;
      const kind = String(row.kind || row.type || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      const labelRaw =
        typeof row.label === "string" && row.label.trim()
          ? row.label.trim()
          : typeof row.name === "string" && row.name.trim()
            ? row.name.trim()
            : null;
      const isHigh =
        kind.includes("high") ||
        kind === "donch_high" ||
        kind === "donchian_high" ||
        String(labelRaw || "")
          .toLowerCase()
          .includes("high");
      const isLow =
        kind.includes("low") ||
        kind === "donch_low" ||
        kind === "donchian_low" ||
        String(labelRaw || "")
          .toLowerCase()
          .includes("low");
      // High/low/mid tags belong on the first-bar rails (already labeled there).
      if (isHigh || isLow || kind.includes("mid") || kind.includes("middle")) continue;

      let markerLabel = labelRaw;
      if (!markerLabel) {
        if (kind.includes("break")) markerLabel = "Break";
        else markerLabel = "Donch";
      }
      const price =
        finiteNum(row.price) ?? finiteNum(row.level) ?? loc.breakLevel ?? upper;
      pushMarker(out, {
        id: `${prefix}:donch-marker:${kind || i}:${barTime}`,
        label: markerLabel,
        barTime,
        price,
        showLabel: true,
      });
    }
  }

  return true;
}

function pushMarker(
  out: StrategyRdWaveCDrawing[],
  opts: {
    id: string;
    label: string;
    barTime: string | null;
    price?: number | null;
    paneId?: string | null;
    showLabel?: boolean;
  }
): void {
  if (!opts.barTime) return;
  out.push({
    id: opts.id,
    label: opts.label,
    kind: "marker",
    barTime: opts.barTime,
    fromBarTime: null,
    toBarTime: null,
    high: null,
    low: null,
    price: opts.price ?? null,
    showLabel: opts.showLabel,
    paneId: opts.paneId ?? null,
  });
}

/** RSI/MACD/Stoch divergence — Alchemy price_line / osc_line + reclaim / structure dashes. */
function pushDivergenceSwings(
  out: StrategyRdWaveCDrawing[],
  opts: {
    prefix: string;
    conceptId: string;
    label: string;
    loc: StrategyRdWaveCLocation;
    meta: Record<string, unknown>;
    candles?: readonly OhlcLike[] | null;
  }
): boolean {
  const { prefix, conceptId, label, loc, meta, candles } = opts;
  const priceLine = loc.divPriceLine;
  const oscLine = loc.divOscLine;
  const times = strategyRdWaveCResolveDivPivotTimes(loc, candles);
  const price1 = priceLine?.p1.price ?? loc.divPrice1;
  const price2 = priceLine?.p2.price ?? loc.divPrice2;
  const t1 = priceLine?.p1.barTime ?? times?.t1 ?? null;
  const t2 = priceLine?.p2.barTime ?? times?.t2 ?? null;
  if (!t1 || !t2 || price1 == null || price2 == null) return false;

  const side = (loc.divSide || loc.direction || "").toLowerCase();
  const bull = side === "bull" || side === "long" || side === "bullish";
  const bear = side === "bear" || side === "short" || side === "bearish";
  const sideKey = bull ? "bull" : bear ? "bear" : "x";
  const tag =
    loc.divLabel ||
    (loc.divClass && (bull || bear)
      ? `${bull ? "Bull" : "Bear"} ${loc.divClass}`
      : label || "Div");
  const oscillator =
    typeof meta.oscillator === "string" ? meta.oscillator : null;
  const oscSeries = strategyRdResolveOscSeries({
    chartDrawOscSeries: loc.chartDrawOscSeries,
    oscillator,
    source: loc.oscSource,
    conceptId,
  });
  // Never slope on the MACD signal line — only macd_line / histogram / rsi / stoch_k.
  const oscPane = strategyRdOscSeriesPaneId(oscSeries);
  const signalBar = loc.barTime || t2;
  const pills =
    loc.chartDrawSignalBarPills.length > 0
      ? loc.chartDrawSignalBarPills
      : ["enter", "div"];

  // Price swing: circles + sloping ray (arrow toward 2nd pivot).
  pushMarker(out, {
    id: `${prefix}:div-p1:${sideKey}`,
    label: "·",
    barTime: t1,
    price: price1,
    showLabel: true,
  });
  pushMarker(out, {
    id: `${prefix}:div-p2:${sideKey}`,
    label: "·",
    barTime: t2,
    price: price2,
    showLabel: true,
  });
  out.push({
    id: `${prefix}:div-price-line:${sideKey}`,
    label: tag,
    kind: "ray",
    barTime: t2,
    fromBarTime: t1,
    toBarTime: t2,
    high: null,
    low: null,
    price: price2,
    fromPrice: price1,
    toPrice: price2,
    lineExtend: "none",
    arrowRight: true,
    showLabel: false,
  });

  // Osc pane: same bars, osc values (FE owns full RSI/MACD/Stoch series).
  const osc1 = oscLine?.p1.price ?? loc.divOsc1;
  const osc2 = oscLine?.p2.price ?? loc.divOsc2;
  const oscT1 = oscLine?.p1.barTime ?? t1;
  const oscT2 = oscLine?.p2.barTime ?? t2;
  if (osc1 != null && osc2 != null && oscT1 && oscT2) {
    pushMarker(out, {
      id: `${prefix}:div-o1:${sideKey}`,
      label: "·",
      barTime: oscT1,
      price: osc1,
      paneId: oscPane,
      showLabel: true,
    });
    pushMarker(out, {
      id: `${prefix}:div-o2:${sideKey}`,
      label: "·",
      barTime: oscT2,
      price: osc2,
      paneId: oscPane,
      showLabel: true,
    });
    out.push({
      id: `${prefix}:div-osc-line:${sideKey}`,
      label: "Osc",
      kind: "ray",
      barTime: oscT2,
      fromBarTime: oscT1,
      toBarTime: oscT2,
      high: null,
      low: null,
      price: osc2,
      fromPrice: osc1,
      toPrice: osc2,
      lineExtend: "none",
      showLabel: false,
      paneId: oscPane,
    });
  }

  // Alchemy reclaim — dashed horizontal at 2nd price pivot (line only, no pill).
  if (loc.divReclaimLevel != null) {
    out.push({
      id: `${prefix}:div-reclaim:${sideKey}`,
      label: "Reclaim",
      kind: "level",
      barTime: t2,
      fromBarTime: t2,
      toBarTime: signalBar,
      high: null,
      low: null,
      price: loc.divReclaimLevel,
      showLabel: false,
    });
  }

  // Intervening opposite swing — dashed structure-break level at that pivot.
  if (loc.divStructureBreakLevel != null) {
    const structurePivotTime =
      loc.divStructureBreakPivotTime ??
      (loc.divStructureBreakPivotIndex != null && candles?.length
        ? candleTime(candles[loc.divStructureBreakPivotIndex]!)
        : null) ??
      t1;
    // Short dash from structure pivot through signal (or reclaim) — keep order.
    const left =
      structurePivotTime && signalBar && structurePivotTime <= signalBar
        ? structurePivotTime
        : t1;
    const right =
      signalBar && signalBar !== left
        ? signalBar
        : t2 !== left
          ? t2
          : signalBar || t2;
    out.push({
      id: `${prefix}:div-structure:${sideKey}`,
      label: bull ? "Pivot high" : bear ? "Pivot low" : "Structure",
      kind: "level",
      barTime: left,
      fromBarTime: left,
      toBarTime: right,
      high: null,
      low: null,
      price: loc.divStructureBreakLevel,
      // Dash only — a far "Pivot low" pill blows the price Y-scale.
      showLabel: false,
    });
    if (loc.divStructureBreakBarTime || loc.divStructureBreakBarIndex != null) {
      const brokenAt =
        loc.divStructureBreakBarTime ??
        (loc.divStructureBreakBarIndex != null && candles?.length
          ? candleTime(candles[loc.divStructureBreakBarIndex]!)
          : null);
      // Pin text to price swing (not the far structure level) so Y stays sane.
      pushMarker(out, {
        id: `${prefix}:div-structure-broken:${sideKey}`,
        label: bull ? "Pivot high broken" : bear ? "Pivot low broken" : "Structure broken",
        barTime: signalBar || brokenAt,
        price: price2,
        showLabel: true,
      });
    }
  }

  // BE markers[] — only narrative chips (divergence / structure / enter).
  // Pivot/osc dots already come from price_line / osc_line; never paint osc Y on price.
  const markersRaw = meta.markers;
  if (Array.isArray(markersRaw)) {
    for (let i = 0; i < markersRaw.length; i += 1) {
      const row = asMetaRecord(markersRaw[i]);
      if (!row) continue;
      const barTime =
        asMetaTime(row.bar_time) ?? asMetaTime(row.time) ?? asMetaTime(row.timestamp);
      if (!barTime) continue;
      const kind = String(row.kind || row.type || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      const labelRaw =
        typeof row.label === "string" && row.label.trim()
          ? row.label.trim()
          : typeof row.name === "string" && row.name.trim()
            ? row.name.trim()
            : null;
      const labelKey = String(labelRaw || "").toLowerCase();
      // Swing / osc endpoints — circles + rays already drawn.
      if (
        kind.includes("div_pivot") ||
        kind.includes("pivot_a") ||
        kind.includes("pivot_b") ||
        kind === "pivot_a" ||
        kind === "pivot_b" ||
        kind === "price_a" ||
        kind === "price_b" ||
        kind.includes("div_osc") ||
        kind === "osc_a" ||
        kind === "osc_b" ||
        kind === "osc_1" ||
        kind === "osc_2" ||
        kind === "pivot1" ||
        kind === "pivot2" ||
        /^osc\s*[12ab]$/i.test(labelKey) ||
        /^pivot\s*[12ab]$/i.test(labelKey) ||
        /^swing\s+(high|low)\s*[12ab]?$/i.test(labelKey) ||
        labelKey === "pivot a" ||
        labelKey === "pivot b" ||
        labelKey === "osc a" ||
        labelKey === "osc b"
      ) {
        continue;
      }
      // Structure / reclaim dashes already carry those tags.
      if (
        labelKey === "pivot low" ||
        labelKey === "pivot high" ||
        labelKey === "reclaim" ||
        kind === "reclaim" ||
        kind === "structure" ||
        kind === "structure_pivot" ||
        kind === "structure_break"
      ) {
        continue;
      }
      // div_label chip already covers the divergence tag.
      if (
        loc.divLabel &&
        (kind.includes("diverg") || kind === "div" || kind === "divergence")
      ) {
        continue;
      }
      let markerLabel = labelRaw;
      if (!markerLabel) {
        if (kind.includes("structure")) {
          markerLabel = bull ? "Pivot high broken" : bear ? "Pivot low broken" : "Structure";
        } else if (kind.includes("reclaim")) markerLabel = "Reclaim";
        else if (kind.includes("diverg") || kind === "div") markerLabel = tag;
        else if (kind.includes("enter")) markerLabel = "Enter";
        else continue; // unknown marker — don't invent a pill
      }
      const paneRaw = typeof row.pane === "string" ? row.pane.trim().toLowerCase() : "";
      const onOsc =
        paneRaw.includes("osc") ||
        paneRaw.includes("rsi") ||
        paneRaw.includes("macd") ||
        paneRaw.includes("stoch") ||
        kind.startsWith("osc_") ||
        labelKey.startsWith("osc");
      // Osc readings on the price pane flatten gold (Y≈50 next to Y≈4200). Skip or route.
      const rawPrice = finiteNum(row.price) ?? finiteNum(row.level);
      if (
        !onOsc &&
        rawPrice != null &&
        price2 != null &&
        price2 > 200 &&
        rawPrice >= 0 &&
        rawPrice <= 100
      ) {
        continue;
      }
      const price = rawPrice ?? (onOsc ? osc2 : price2);
      pushMarker(out, {
        id: `${prefix}:div-marker:${kind || i}:${barTime}`,
        label: markerLabel,
        barTime,
        price,
        paneId: onOsc ? oscPane : null,
        showLabel: true,
      });
    }
  }

  // Signal-bar pills from chart_draw — Div only here.
  // Enter is owned by path / concept drawings (direction-aware, one per hit).
  if (signalBar) {
    const wantDiv = pills.includes("div");
    if (wantDiv && loc.divLabel) {
      pushMarker(out, {
        id: `${prefix}:div-label:${sideKey}`,
        label: loc.divLabel,
        barTime: signalBar,
        price: price2,
        showLabel: true,
      });
    } else if (wantDiv) {
      pushMarker(out, {
        id: `${prefix}:div-pill:${sideKey}`,
        label: "Div",
        barTime: signalBar,
        price: price2,
        showLabel: true,
      });
    } else if (loc.divLabel) {
      pushMarker(out, {
        id: `${prefix}:div-label:${sideKey}`,
        label: loc.divLabel,
        barTime: t2,
        price: price2,
        showLabel: true,
      });
    }
  }

  return true;
}

/** MACD signal-line / zero-line cross — Cross|Zero pills; Enter owned by path. */
function pushMacdCrossZero(
  out: StrategyRdWaveCDrawing[],
  opts: {
    prefix: string;
    conceptId: string;
    loc: StrategyRdWaveCLocation;
  }
): boolean {
  const { prefix, conceptId, loc } = opts;
  if (!loc.barTime) return false;
  const kind =
    (loc.chartDrawKind || "").trim().toLowerCase() ||
    (conceptId.toLowerCase().includes("zero") ? "macd_zero" : "macd_cross");
  const pills =
    loc.chartDrawSignalBarPills.length > 0
      ? loc.chartDrawSignalBarPills
      : kind === "macd_zero"
        ? ["enter", "zero"]
        : ["enter", "cross"];
  const price = loc.macd ?? loc.macdHist ?? loc.breakLevel ?? null;
  // Skip "enter" — path / concept drawings stamp one direction-colored Enter.
  for (const pill of pills) {
    if (pill === "enter") continue;
    const label = pill === "zero" ? "Zero" : pill === "cross" ? "Cross" : pill;
    pushMarker(out, {
      id: `${prefix}:macd-${pill}`,
      label,
      barTime: loc.barTime,
      price,
      paneId: price != null ? "tessera-macd" : null,
      showLabel: true,
    });
  }
  return true;
}

/** MACD values scale with price (EURUSD ~0.0004, gold ~5): 2 decimals, or 3 significant. */
function formatMacdValue(v: number, signed: boolean): string {
  const abs = Math.abs(v);
  const body = abs >= 1 ? abs.toFixed(2) : Number(abs.toPrecision(3)).toString();
  if (!signed) return `${v < 0 ? "\u2212" : ""}${body}`;
  return `${v > 0 ? "+" : v < 0 ? "\u2212" : ""}${body}`;
}

/** Confluence-only oscillator badge (no full div / cross geometry). */
function strategyRdOscConfluenceBadge(conceptId: string, loc: StrategyRdWaveCLocation): string {
  const id = conceptId.trim().toLowerCase();
  // MACD confluence options (Momentum / Trend / Momentum improving) — show the tested value.
  if (id === "macd_momentum" && loc.macdHist != null) {
    return `MACD hist ${formatMacdValue(loc.macdHist, true)}`;
  }
  if (id === "macd_trend" && loc.macd != null) {
    return `MACD line ${formatMacdValue(loc.macd, true)}`;
  }
  if (id === "macd_improving" && loc.macdHist != null) {
    if (loc.macdHistPrev == null) return `MACD hist ${formatMacdValue(loc.macdHist, true)}`;
    const arrow = loc.macdHist >= loc.macdHistPrev ? "\u2197" : "\u2198";
    return `MACD hist ${arrow} ${formatMacdValue(loc.macdHistPrev, false)}\u2192${formatMacdValue(loc.macdHist, false)}`;
  }
  if (id.includes("hist_up") || loc.bucket === "hist_up") return "Hist+";
  if (id.includes("hist_down") || loc.bucket === "hist_down") return "Hist−";
  if (id.includes("macd_cross") || id.includes("cross_bull") || id.includes("cross_bear")) {
    return "MACD×sig";
  }
  if (id.includes("macd_zero") || id.includes("zero_bull") || id.includes("zero_bear")) {
    return "MACD×0";
  }
  if (id.includes("oversold") || loc.bucket === "oversold") {
    if (id.includes("stoch")) return "Stoch OS";
    if (id.includes("rsi")) return "RSI OS";
    return "OS";
  }
  if (id.includes("overbought") || loc.bucket === "overbought") {
    if (id.includes("stoch")) return "Stoch OB";
    if (id.includes("rsi")) return "RSI OB";
    return "OB";
  }
  if (strategyRdWaveCIsDivergenceId(id)) return "Div";
  return strategyRdConceptChartLabel(id) || id;
}

/** Build Wave C drawings from a BE meta / evidence row. Returns [] when nothing drawable. */
function finiteMetaNum(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Open time of the candle just before `barTime` in the chart series (null when absent). */
function previousCandleTime(
  candles: readonly OhlcLike[] | null | undefined,
  barTime: string | null
): string | null {
  const target = Date.parse((barTime || "").trim().replace(/\+00:00$/, "Z"));
  if (!candles?.length || !Number.isFinite(target)) return null;
  for (let i = candles.length - 1; i > 0; i -= 1) {
    const c = candles[i] as { timestamp?: string | null; time?: string | null };
    const t = Date.parse(((c.timestamp || c.time || "") as string).trim().replace(/\+00:00$/, "Z"));
    if (t !== target) continue;
    const prev = candles[i - 1] as { timestamp?: string | null; time?: string | null };
    const prevTime = ((prev.timestamp || prev.time || "") as string).trim();
    return prevTime || null;
  }
  return null;
}

export function strategyRdWaveCDrawingsFromMeta(opts: {
  prefix: string;
  conceptId: string;
  meta: Record<string, unknown> | null | undefined;
  label?: string | null;
  candles?: readonly OhlcLike[] | null;
  sessions?: readonly StrategyRdSessionOption[] | null;
}): StrategyRdWaveCDrawing[] {
  const id = opts.conceptId.trim();
  if (!id || !opts.meta) return [];
  const loc = parseStrategyRdWaveCLocation(opts.meta);
  if (loc.softPass) return [];

  const label = (opts.label || strategyRdConceptChartLabel(id) || id).trim() || id;
  const out: StrategyRdWaveCDrawing[] = [];
  const prefix = opts.prefix;
  const isWedge = strategyRdWaveCIsWedgeId(id);
  const isCup = strategyRdWaveCIsCupId(id);
  const isPennant = strategyRdWaveCIsPennantMeta(id, loc);
  const isFlag = !isPennant && strategyRdWaveCIsFlagId(id);
  const isPoleChannel = (isFlag || isPennant) && strategyRdWaveCHasFlagGeometry(loc);
  const isRectangle = strategyRdWaveCIsRectangleMeta(id, loc);
  const isTriangle = !isRectangle && strategyRdWaveCIsTriangleMeta(id, loc);
  const isTriRectLines =
    (isTriangle || isRectangle) && strategyRdWaveCHasWedgeLines(loc);
  const isDonchian =
    strategyRdWaveCIsDonchianId(id) || strategyRdWaveCIsDonchianChartDraw(loc);
  const isDivergence =
    strategyRdWaveCIsDivergenceChartDraw(loc) ||
    strategyRdWaveCIsDivergenceId(id) ||
    strategyRdWaveCHasDivergenceSwings(loc, opts.candles);
  const isMacdCrossZero =
    strategyRdWaveCIsMacdCrossZeroChartDraw(loc) ||
    strategyRdWaveCIsMacdCrossZeroId(id);

  // --- Donchian channel: flat upper/lower over channel window (never Enter-bar level box) ---
  if (isDonchian) {
    const drew = pushDonchianChannel(out, {
      prefix,
      label: label.includes("Donch") ? label : "Donch",
      loc,
      meta: opts.meta,
    });
    if (drew) return out;
    // Geometry missing (stale match) — pill only; do not invent a vertical level box.
    if (loc.barTime) {
      pushMarker(out, {
        id: `${prefix}:donch`,
        label: "Donch",
        barTime: loc.barTime,
        price: loc.breakLevel ?? loc.donchianHigh ?? loc.donchianLow,
        showLabel: true,
      });
    }
    return out;
  }

  // --- Divergence: Alchemy price_line / osc_line + reclaim / structure ---
  if (isDivergence) {
    const drew = pushDivergenceSwings(out, {
      prefix,
      conceptId: id,
      label,
      loc,
      meta: opts.meta,
      candles: opts.candles,
    });
    if (drew) return out;
    // Missing swing geometry — Enter / concept pill only.
    if (loc.barTime) {
      pushMarker(out, {
        id: `${prefix}:enter`,
        label: strategyRdWaveCIsDivergenceId(id) ? "Div" : "Enter",
        barTime: loc.barTime,
        price: loc.divPrice2 ?? loc.rsi,
        showLabel: true,
      });
    }
    return out;
  }

  // --- MACD cross / zero: Cross|Zero on the MACD pane. Enter stays on the path. ---
  if (isMacdCrossZero) {
    pushMacdCrossZero(out, { prefix, conceptId: id, loc });
    return out;
  }

  // --- Candle span (not wedges/cups/flags/pennants/triangles/rectangles) ---
  if (
    !isWedge &&
    !isCup &&
    !isFlag &&
    !isPennant &&
    !isTriangle &&
    !isRectangle &&
    (strategyRdWaveCIsCandlePatternId(id) || strategyRdWaveCHasCandleSpan(loc))
  ) {
    // Engulfing / inside bar are two-candle patterns without span keys: include the
    // engulfed / mother candle (the bar before `bar_time`). Inside bar boxes the mother range.
    const idLower = id.toLowerCase();
    const twoCandle =
      (idLower.includes("engulf") || idLower === "inside_bar") &&
      !loc.spanStartTime &&
      !loc.patternStartTime;
    const priorBar = twoCandle ? previousCandleTime(opts.candles, loc.barTime) : null;
    const motherHigh = idLower === "inside_bar" ? finiteMetaNum(opts.meta.mother_high) : null;
    const motherLow = idLower === "inside_bar" ? finiteMetaNum(opts.meta.mother_low) : null;
    const from = loc.spanStartTime ?? loc.patternStartTime ?? priorBar ?? loc.barTime;
    const to = loc.spanEndTime ?? loc.patternEndTime ?? loc.barTime;
    const high = loc.patternHigh ?? motherHigh;
    const low = loc.patternLow ?? motherLow;
    if (from && to && high != null && low != null && high > low) {
      out.push({
        id: `${prefix}:span`,
        label,
        kind: "bar",
        barTime: loc.barTime ?? to,
        fromBarTime: from,
        toBarTime: to,
        high,
        low,
        price: (high + low) / 2,
        showLabel: true,
      });
    } else if (from && to) {
      out.push({
        id: `${prefix}:span`,
        label,
        kind: "bar",
        barTime: loc.barTime ?? to,
        fromBarTime: from,
        toBarTime: to,
        high: high,
        low: low,
        price: high != null && low != null ? (high + low) / 2 : null,
        showLabel: true,
      });
    } else if (loc.barTime) {
      pushMarker(out, {
        id: `${prefix}:hit`,
        label,
        barTime: loc.barTime,
        price: high != null && low != null ? (high + low) / 2 : null,
      });
    }
  }

  // --- Wedge: converging upper_line / lower_line (not neckline / flat box) ---
  if (isWedge && strategyRdWaveCHasWedgeLines(loc) && loc.upperLine && loc.lowerLine) {
    const upperSeg = trendLineThrough(loc.upperLine, loc.barTime, loc.barIndex);
    const lowerSeg = trendLineThrough(loc.lowerLine, loc.barTime, loc.barIndex);
    out.push({
      id: `${prefix}:upper`,
      label: "",
      kind: "ray",
      barTime: upperSeg.toBarTime,
      fromBarTime: upperSeg.fromBarTime,
      toBarTime: upperSeg.toBarTime,
      high: null,
      low: null,
      fromPrice: upperSeg.fromPrice,
      toPrice: upperSeg.toPrice,
      price: (upperSeg.fromPrice + upperSeg.toPrice) / 2,
      lineExtend: "none",
      showLabel: false,
    });
    out.push({
      id: `${prefix}:lower`,
      label: "",
      kind: "ray",
      barTime: lowerSeg.toBarTime,
      fromBarTime: lowerSeg.fromBarTime,
      toBarTime: lowerSeg.toBarTime,
      high: null,
      low: null,
      fromPrice: lowerSeg.fromPrice,
      toPrice: lowerSeg.toPrice,
      price: (lowerSeg.fromPrice + lowerSeg.toPrice) / 2,
      lineExtend: "none",
      showLabel: false,
    });

    const bodyFrom = loc.patternStartTime ?? loc.upperLine.p1.barTime;
    const bodyTo = loc.patternEndTime ?? loc.upperLine.p2.barTime;
    const startIdx = loc.patternStart ?? loc.upperLine.p1.barIndex;
    const endIdx = loc.patternEnd ?? loc.upperLine.p2.barIndex;
    const yU0 = strategyRdTrendLinePriceAt(loc.upperLine, startIdx) ?? loc.upperLine.p1.price;
    const yU1 = strategyRdTrendLinePriceAt(loc.upperLine, endIdx) ?? loc.upperLine.p2.price;
    const yL0 = strategyRdTrendLinePriceAt(loc.lowerLine, startIdx) ?? loc.lowerLine.p1.price;
    const yL1 = strategyRdTrendLinePriceAt(loc.lowerLine, endIdx) ?? loc.lowerLine.p2.price;
    const shadeHi = Math.max(yU0, yU1, yL0, yL1);
    const shadeLo = Math.min(yU0, yU1, yL0, yL1);
    if (bodyFrom && bodyTo && shadeHi > shadeLo) {
      out.push({
        id: `${prefix}:wedge`,
        label: "Wedge",
        kind: "zone",
        barTime: loc.barTime ?? bodyTo,
        fromBarTime: bodyFrom,
        toBarTime: bodyTo,
        high: shadeHi,
        low: shadeLo,
        price: (shadeHi + shadeLo) / 2,
        showLabel: true,
      });
    } else {
      pushMarker(out, {
        id: `${prefix}:label`,
        label: "Wedge",
        barTime: loc.patternEndTime ?? loc.barTime,
        price: (loc.upperLine.p2.price + loc.lowerLine.p2.price) / 2,
        showLabel: true,
      });
    }

    for (let i = 0; i < loc.pivots.length; i += 1) {
      const p = loc.pivots[i]!;
      const pivotLabel =
        p.kind === "high" ? "H" : p.kind === "low" ? "L" : (p.kind || "P").slice(0, 4);
      pushMarker(out, {
        id: `${prefix}:pivot:${i}`,
        label: pivotLabel,
        barTime: p.barTime,
        price: p.price,
        showLabel: true,
      });
    }

    if (loc.breakLevel != null && loc.barTime) {
      out.push({
        id: `${prefix}:break-level`,
        label: "",
        kind: "level",
        barTime: loc.barTime,
        fromBarTime: loc.barTime,
        toBarTime: loc.barTime,
        high: null,
        low: null,
        price: loc.breakLevel,
        showLabel: false,
      });
    }
    // Enter marker comes from path / concept drawings (direction-aware triangle).
  }

  // --- Triangle / Rectangle: upper_line + lower_line (Investopedia; no neckline box) ---
  if (isTriRectLines && loc.upperLine && loc.lowerLine) {
    const patternLabel = strategyRdWaveCTrianglePatternLabel(id, loc);
    // Each line is its own p1→p2 segment (extended to Enter). Never fan from one corner.
    const upperSeg = trendLineThrough(loc.upperLine, loc.barTime, loc.barIndex);
    const lowerSeg = trendLineThrough(loc.lowerLine, loc.barTime, loc.barIndex);
    out.push({
      id: `${prefix}:upper`,
      label: "",
      kind: "ray",
      barTime: upperSeg.toBarTime,
      fromBarTime: upperSeg.fromBarTime,
      toBarTime: upperSeg.toBarTime,
      high: null,
      low: null,
      fromPrice: upperSeg.fromPrice,
      toPrice: upperSeg.toPrice,
      price: (upperSeg.fromPrice + upperSeg.toPrice) / 2,
      lineExtend: "none",
      showLabel: false,
    });
    out.push({
      id: `${prefix}:lower`,
      label: "",
      kind: "ray",
      barTime: lowerSeg.toBarTime,
      fromBarTime: lowerSeg.fromBarTime,
      toBarTime: lowerSeg.toBarTime,
      high: null,
      low: null,
      fromPrice: lowerSeg.fromPrice,
      toPrice: lowerSeg.toPrice,
      price: (lowerSeg.fromPrice + lowerSeg.toPrice) / 2,
      lineExtend: "none",
      showLabel: false,
    });

    // Rectangle: optional horizontal box between the rails for the body window.
    // Triangles: never draw a huge pattern_high/low box — lines only.
    if (isRectangle) {
      const bodyFrom = loc.patternStartTime ?? loc.upperLine.p1.barTime;
      const bodyTo = loc.patternEndTime ?? loc.upperLine.p2.barTime;
      const startIdx = loc.patternStart ?? loc.upperLine.p1.barIndex;
      const endIdx = loc.patternEnd ?? loc.upperLine.p2.barIndex;
      const yU0 = strategyRdTrendLinePriceAt(loc.upperLine, startIdx) ?? loc.upperLine.p1.price;
      const yU1 = strategyRdTrendLinePriceAt(loc.upperLine, endIdx) ?? loc.upperLine.p2.price;
      const yL0 = strategyRdTrendLinePriceAt(loc.lowerLine, startIdx) ?? loc.lowerLine.p1.price;
      const yL1 = strategyRdTrendLinePriceAt(loc.lowerLine, endIdx) ?? loc.lowerLine.p2.price;
      const shadeHi = Math.max(yU0, yU1, yL0, yL1);
      const shadeLo = Math.min(yU0, yU1, yL0, yL1);
      if (bodyFrom && bodyTo && shadeHi > shadeLo) {
        out.push({
          id: `${prefix}:rectangle`,
          label: patternLabel,
          kind: "zone",
          barTime: loc.barTime ?? bodyTo,
          fromBarTime: bodyFrom,
          toBarTime: bodyTo,
          high: shadeHi,
          low: shadeLo,
          price: (shadeHi + shadeLo) / 2,
          showLabel: true,
        });
      } else {
        pushMarker(out, {
          id: `${prefix}:label`,
          label: patternLabel,
          barTime: loc.patternEndTime ?? loc.barTime,
          price: (loc.upperLine.p2.price + loc.lowerLine.p2.price) / 2,
          showLabel: true,
        });
      }
    } else {
      pushMarker(out, {
        id: `${prefix}:label`,
        label: patternLabel,
        barTime: loc.patternEndTime ?? loc.upperLine.p2.barTime ?? loc.barTime,
        price: (loc.upperLine.p2.price + loc.lowerLine.p2.price) / 2,
        showLabel: true,
      });
    }

    if (loc.breakLevel != null && loc.barTime) {
      out.push({
        id: `${prefix}:break-level`,
        label: "",
        kind: "level",
        barTime: loc.barTime,
        fromBarTime: loc.barTime,
        toBarTime: loc.barTime,
        high: null,
        low: null,
        price: loc.breakLevel,
        showLabel: false,
      });
    }
    // Enter on bar_time by direction comes from path highlights.
  }

  // --- Flag / pennant: pole + channel (flag=parallel, pennant=converging triangle) ---
  if (isPoleChannel) {
    const patternLabel = isPennant ? "Pennant" : "Flag";
    const zoneId = isPennant ? "pennant" : "flag";

    if (loc.pole) {
      out.push({
        id: `${prefix}:pole`,
        label: "",
        kind: "ray",
        barTime: loc.pole.p2.barTime,
        fromBarTime: loc.pole.p1.barTime,
        toBarTime: loc.pole.p2.barTime,
        high: null,
        low: null,
        fromPrice: loc.pole.p1.price,
        toPrice: loc.pole.p2.price,
        price: (loc.pole.p1.price + loc.pole.p2.price) / 2,
        lineExtend: "none",
        showLabel: false,
      });
    }

    if (loc.upperLine && loc.lowerLine) {
      // Each line is its own p1→p2 segment (extended to Enter). Never fan from one corner.
      const upperSeg = trendLineThrough(loc.upperLine, loc.barTime, loc.barIndex);
      const lowerSeg = trendLineThrough(loc.lowerLine, loc.barTime, loc.barIndex);
      out.push({
        id: `${prefix}:upper`,
        label: "",
        kind: "ray",
        barTime: upperSeg.toBarTime,
        fromBarTime: upperSeg.fromBarTime,
        toBarTime: upperSeg.toBarTime,
        high: null,
        low: null,
        fromPrice: upperSeg.fromPrice,
        toPrice: upperSeg.toPrice,
        price: (upperSeg.fromPrice + upperSeg.toPrice) / 2,
        lineExtend: "none",
        showLabel: false,
      });
      out.push({
        id: `${prefix}:lower`,
        label: "",
        kind: "ray",
        barTime: lowerSeg.toBarTime,
        fromBarTime: lowerSeg.fromBarTime,
        toBarTime: lowerSeg.toBarTime,
        high: null,
        low: null,
        fromPrice: lowerSeg.fromPrice,
        toPrice: lowerSeg.toPrice,
        price: (lowerSeg.fromPrice + lowerSeg.toPrice) / 2,
        lineExtend: "none",
        showLabel: false,
      });

      // Fill only the body window (flag/pennant), never pattern_high/low or the pole.
      const bodyFrom = loc.flagStartTime ?? loc.upperLine.p1.barTime;
      const bodyTo = loc.flagEndTime ?? loc.upperLine.p2.barTime;
      const startIdx = loc.flagStart ?? loc.upperLine.p1.barIndex;
      const endIdx = loc.flagEnd ?? loc.upperLine.p2.barIndex;
      const yU0 = strategyRdTrendLinePriceAt(loc.upperLine, startIdx) ?? loc.upperLine.p1.price;
      const yU1 = strategyRdTrendLinePriceAt(loc.upperLine, endIdx) ?? loc.upperLine.p2.price;
      const yL0 = strategyRdTrendLinePriceAt(loc.lowerLine, startIdx) ?? loc.lowerLine.p1.price;
      const yL1 = strategyRdTrendLinePriceAt(loc.lowerLine, endIdx) ?? loc.lowerLine.p2.price;
      const shadeHi = Math.max(yU0, yU1, yL0, yL1);
      const shadeLo = Math.min(yU0, yU1, yL0, yL1);
      if (bodyFrom && bodyTo && shadeHi > shadeLo) {
        out.push({
          id: `${prefix}:${zoneId}`,
          label: patternLabel,
          kind: "zone",
          barTime: loc.barTime ?? bodyTo,
          fromBarTime: bodyFrom,
          toBarTime: bodyTo,
          high: shadeHi,
          low: shadeLo,
          price: (shadeHi + shadeLo) / 2,
          showLabel: true,
        });
      } else {
        pushMarker(out, {
          id: `${prefix}:label`,
          label: patternLabel,
          barTime: loc.flagEndTime ?? loc.barTime,
          price: (loc.upperLine.p2.price + loc.lowerLine.p2.price) / 2,
          showLabel: true,
        });
      }
    } else {
      pushMarker(out, {
        id: `${prefix}:label`,
        label: patternLabel,
        barTime: loc.poleEndTime ?? loc.barTime,
        price: loc.pole ? loc.pole.p2.price : null,
        showLabel: true,
      });
    }

    if (loc.breakLevel != null && loc.barTime) {
      out.push({
        id: `${prefix}:break-level`,
        label: "",
        kind: "level",
        barTime: loc.barTime,
        fromBarTime: loc.barTime,
        toBarTime: loc.barTime,
        high: null,
        low: null,
        price: loc.breakLevel,
        showLabel: false,
      });
    }
  }

  // --- H&S / double / triple (pivots + neckline) — not wedges/cups/flags/pennants/tri/rect ---
  if (
    !isWedge &&
    !isCup &&
    !isFlag &&
    !isPennant &&
    !isTriangle &&
    !isRectangle &&
    (strategyRdWaveCHasNecklinePattern(loc) ||
      (strategyRdWaveCIsChartPatternId(id) && loc.neckline != null))
  ) {
    const span = formationSpan(loc);
    const namedShoulders =
      loc.leftShoulderTime &&
      loc.headTime &&
      loc.rightShoulderTime &&
      loc.leftShoulder != null &&
      loc.head != null &&
      loc.rightShoulder != null;

    if (namedShoulders) {
      pushMarker(out, {
        id: `${prefix}:ls`,
        label: "Left S",
        barTime: loc.leftShoulderTime,
        price: loc.leftShoulder,
        showLabel: true,
      });
      pushMarker(out, {
        id: `${prefix}:head`,
        label: "Head",
        barTime: loc.headTime,
        price: loc.head,
        showLabel: true,
      });
      pushMarker(out, {
        id: `${prefix}:rs`,
        label: "Right S",
        barTime: loc.rightShoulderTime,
        price: loc.rightShoulder,
        showLabel: true,
      });
      // Silhouette segments: Left shoulder → Head → Right shoulder (no ray extend).
      out.push({
        id: `${prefix}:ls-head`,
        label: "",
        kind: "ray",
        barTime: loc.headTime!,
        fromBarTime: loc.leftShoulderTime,
        toBarTime: loc.headTime,
        high: null,
        low: null,
        fromPrice: loc.leftShoulder!,
        toPrice: loc.head!,
        price: (loc.leftShoulder! + loc.head!) / 2,
        lineExtend: "none",
        showLabel: false,
      });
      out.push({
        id: `${prefix}:head-rs`,
        label: "",
        kind: "ray",
        barTime: loc.rightShoulderTime!,
        fromBarTime: loc.headTime,
        toBarTime: loc.rightShoulderTime,
        high: null,
        low: null,
        fromPrice: loc.head!,
        toPrice: loc.rightShoulder!,
        price: (loc.head! + loc.rightShoulder!) / 2,
        lineExtend: "none",
        showLabel: false,
      });
    } else {
      for (let i = 0; i < loc.pivots.length; i += 1) {
        const p = loc.pivots[i]!;
        const pivotLabel =
          p.kind === "high" ? "H" : p.kind === "low" ? "L" : (p.kind || "P").slice(0, 4);
        pushMarker(out, {
          id: `${prefix}:pivot:${i}`,
          label: pivotLabel,
          barTime: p.barTime,
          price: p.price,
          showLabel: true,
        });
      }
    }

    // Formation box so the whole structure is findable (not just Neck).
    if (
      span.from &&
      span.to &&
      loc.patternHigh != null &&
      loc.patternLow != null &&
      loc.patternHigh > loc.patternLow &&
      !out.some((d) => d.id === `${prefix}:span`)
    ) {
      out.push({
        id: `${prefix}:span`,
        label,
        kind: "bar",
        barTime: loc.barTime ?? span.to,
        fromBarTime: span.from,
        toBarTime: span.to,
        high: loc.patternHigh,
        low: loc.patternLow,
        price: (loc.patternHigh + loc.patternLow) / 2,
        showLabel: true,
      });
    }

    const sloped = strategyRdWaveCNecklineSegment(loc);
    if (sloped) {
      out.push({
        id: `${prefix}:neckline`,
        label: "Neckline",
        kind: "ray",
        barTime: sloped.toBarTime,
        fromBarTime: sloped.fromBarTime,
        toBarTime: sloped.toBarTime,
        high: null,
        low: null,
        fromPrice: sloped.fromPrice,
        toPrice: sloped.toPrice,
        price: (sloped.fromPrice + sloped.toPrice) / 2,
        lineExtend: "none",
        showLabel: true,
      });
    } else if (loc.neckline != null && (span.from || loc.barTime)) {
      // Flat neckline fallback (double / triple / older meta): from the neck pivot to the break.
      const neckTo = loc.barTime ?? span.to;
      const neckPivot = loc.pivots.find(
        (p) => p.barTime && Math.abs(p.price - loc.neckline!) <= Math.abs(loc.neckline!) * 1e-9
      );
      const rawNeckTime = (opts.meta as Record<string, unknown>).neck_time;
      const neckFrom =
        neckPivot?.barTime ??
        (typeof rawNeckTime === "string" && rawNeckTime.trim() ? rawNeckTime.trim() : null) ??
        span.from;
      out.push({
        id: `${prefix}:neckline`,
        label: "Neckline",
        kind: "level",
        barTime: neckTo,
        fromBarTime: neckFrom,
        toBarTime: neckTo,
        high: null,
        low: null,
        price: loc.neckline,
      });
    }
    // Entry / break is owned by path highlights ("Enter") — do not add a
    // competing "Break" pill on the same signal bar.
  }

  // --- Channel (legacy bound+slope; wedges/flags/pennants/tri/rect use upper_line above) ---
  if (
    !isWedge &&
    !isPoleChannel &&
    !isTriRectLines &&
    (strategyRdWaveCHasChannel(loc) ||
      (strategyRdWaveCIsChartPatternId(id) && loc.upperBound != null))
  ) {
    const span = formationSpan(loc);
    const startIdx =
      loc.patternStart ??
      loc.flagStart ??
      loc.poleStart ??
      loc.pivots[0]?.barIndex ??
      loc.barIndex ??
      0;
    const endIdx =
      loc.patternEnd ??
      loc.flagEnd ??
      loc.barIndex ??
      loc.pivots[loc.pivots.length - 1]?.barIndex ??
      startIdx + 1;
    const fromT = span.from ?? loc.flagStartTime ?? loc.poleStartTime ?? loc.barTime;
    const toT = span.to ?? loc.flagEndTime ?? loc.barTime;
    if (
      loc.upperBound != null &&
      loc.lowerBound != null &&
      fromT &&
      toT &&
      endIdx !== startIdx
    ) {
      const y1u = strategyRdChannelPriceAt(loc.upperBound, loc.upperSlope, startIdx, startIdx);
      const y2u = strategyRdChannelPriceAt(loc.upperBound, loc.upperSlope, endIdx, startIdx);
      const y1l = strategyRdChannelPriceAt(loc.lowerBound, loc.lowerSlope, startIdx, startIdx);
      const y2l = strategyRdChannelPriceAt(loc.lowerBound, loc.lowerSlope, endIdx, startIdx);
      out.push({
        id: `${prefix}:upper`,
        label: "Upper",
        kind: "ray",
        barTime: toT,
        fromBarTime: fromT,
        toBarTime: toT,
        high: null,
        low: null,
        fromPrice: y1u,
        toPrice: y2u,
        price: (y1u + y2u) / 2,
        showLabel: false,
      });
      out.push({
        id: `${prefix}:lower`,
        label: "Lower",
        kind: "ray",
        barTime: toT,
        fromBarTime: fromT,
        toBarTime: toT,
        high: null,
        low: null,
        fromPrice: y1l,
        toPrice: y2l,
        price: (y1l + y2l) / 2,
        showLabel: false,
      });
      const shadeHi = Math.max(y1u, y2u, y1l, y2l);
      const shadeLo = Math.min(y1u, y2u, y1l, y2l);
      if (shadeHi > shadeLo) {
        out.push({
          id: `${prefix}:channel`,
          label,
          kind: "zone",
          barTime: loc.barTime ?? toT,
          fromBarTime: fromT,
          toBarTime: toT,
          high: shadeHi,
          low: shadeLo,
          price: (shadeHi + shadeLo) / 2,
          showLabel: true,
        });
      }
    }
    if (loc.barTime) {
      pushMarker(out, { id: `${prefix}:break`, label, barTime: loc.barTime });
    }
  }

  // --- Cup & handle: cup_path polyline + flat rim + handle (not rectangle / X diagonals) ---
  if (isCup && strategyRdWaveCHasCup(loc)) {
    const pathPts =
      loc.cupPath.length >= 2
        ? loc.cupPath
        : (() => {
            // Legacy fallback: left → bottom → right only when cup_path missing.
            const leftT = loc.cupLeftTime;
            const rightT = loc.cupRightTime;
            const bottomT = loc.cupBottomTime;
            const bottom = loc.cupLow ?? loc.cupHigh;
            const rim = loc.rim;
            if (!leftT || !rightT || bottom == null || rim == null) return [];
            return [
              { barTime: leftT, price: rim, barIndex: null, kind: "high" },
              {
                barTime: bottomT ?? leftT,
                price: bottom,
                barIndex: null,
                kind: "low",
              },
              { barTime: rightT, price: rim, barIndex: null, kind: "high" },
            ];
          })();

    for (let i = 0; i < pathPts.length - 1; i += 1) {
      const a = pathPts[i]!;
      const b = pathPts[i + 1]!;
      if (a.barTime === b.barTime && a.price === b.price) continue;
      out.push({
        id: `${prefix}:cup-seg:${i}`,
        label: "",
        kind: "ray",
        barTime: b.barTime,
        fromBarTime: a.barTime,
        toBarTime: b.barTime,
        high: null,
        low: null,
        fromPrice: a.price,
        toPrice: b.price,
        price: (a.price + b.price) / 2,
        lineExtend: "none",
        showLabel: false,
      });
    }

    const leftRim = loc.pivots[0] ?? pathPts[0] ?? null;
    const bottom = loc.pivots[1] ?? null;
    const rightRim = loc.pivots[2] ?? pathPts[pathPts.length - 1] ?? null;
    const handle =
      loc.pivots[3] ??
      (loc.handleBarTime && (loc.handlePrice != null || loc.rim != null)
        ? {
            barTime: loc.handleBarTime,
            price: loc.handlePrice ?? loc.rim!,
            barIndex: null,
            kind: null,
          }
        : null);

    const rimFrom =
      loc.cupLeftTime ?? leftRim?.barTime ?? pathPts[0]?.barTime ?? null;
    const rimTo = loc.barTime ?? loc.handleBarTime ?? rightRim?.barTime ?? null;
    if (loc.rim != null && rimFrom && rimTo) {
      out.push({
        id: `${prefix}:rim`,
        label: "Rim",
        kind: "level",
        barTime: rimTo,
        fromBarTime: rimFrom,
        toBarTime: rimTo,
        high: null,
        low: null,
        price: loc.rim,
        showLabel: true,
      });
    }

    if (rightRim && handle && rightRim.barTime !== handle.barTime) {
      out.push({
        id: `${prefix}:handle-seg`,
        label: "Handle",
        kind: "ray",
        barTime: handle.barTime,
        fromBarTime: rightRim.barTime,
        toBarTime: handle.barTime,
        high: null,
        low: null,
        fromPrice: rightRim.price,
        toPrice: handle.price,
        price: (rightRim.price + handle.price) / 2,
        lineExtend: "none",
        showLabel: true,
      });
    } else if (handle) {
      pushMarker(out, {
        id: `${prefix}:handle`,
        label: "Handle",
        barTime: handle.barTime,
        price: handle.price,
        showLabel: true,
      });
    }

    if (leftRim) {
      pushMarker(out, {
        id: `${prefix}:left-rim`,
        label: "Rim",
        barTime: leftRim.barTime,
        price: leftRim.price,
        showLabel: false,
      });
    }
    if (bottom) {
      pushMarker(out, {
        id: `${prefix}:bottom`,
        label: "Cup",
        barTime: bottom.barTime,
        price: bottom.price,
        showLabel: true,
      });
    }
    // Enter comes from path / concept drawings.
  }

  // --- BB / MA bands at evidence (Donchian uses upper_line / lower_line above) ---
  if (
    !isDonchian &&
    (strategyRdWaveCHasBands(loc) || strategyRdWaveCIsBandId(id))
  ) {
    const t = loc.barTime;
    if (t) {
      const bandLabel = loc.zone || label;
      if (loc.upper != null) {
        out.push({
          id: `${prefix}:bb-upper`,
          label: "BB↑",
          kind: "level",
          barTime: t,
          fromBarTime: t,
          toBarTime: t,
          high: null,
          low: null,
          price: loc.upper,
          showLabel: false,
        });
      }
      if (loc.mid != null) {
        out.push({
          id: `${prefix}:bb-mid`,
          label: "BB·",
          kind: "level",
          barTime: t,
          fromBarTime: t,
          toBarTime: t,
          high: null,
          low: null,
          price: loc.mid,
          showLabel: false,
        });
      }
      if (loc.lower != null) {
        out.push({
          id: `${prefix}:bb-lower`,
          label: "BB↓",
          kind: "level",
          barTime: t,
          fromBarTime: t,
          toBarTime: t,
          high: null,
          low: null,
          price: loc.lower,
          showLabel: false,
        });
      }
      if (loc.upper != null && loc.lower != null && loc.upper > loc.lower) {
        out.push({
          id: `${prefix}:bb-zone`,
          label: bandLabel,
          kind: "zone",
          barTime: t,
          fromBarTime: t,
          toBarTime: t,
          high: loc.upper,
          low: loc.lower,
          price: loc.mid ?? (loc.upper + loc.lower) / 2,
          showLabel: true,
        });
      }
      if (loc.maValue != null) {
        out.push({
          id: `${prefix}:ma`,
          label: "MA",
          kind: "level",
          barTime: t,
          fromBarTime: t,
          toBarTime: t,
          high: null,
          low: null,
          price: loc.maValue,
        });
      }
    }
  }

  // --- Killzone session shade ---
  if (strategyRdWaveCIsKillzoneId(id) && loc.barTime) {
    const sid = loc.sessionId;
    const session = (opts.sessions ?? []).find(
      (s) => s.id.trim().toLowerCase() === (sid || "").toLowerCase()
    );
    const startH = session?.start_hour;
    const endH = session?.end_hour;
    if (startH != null && endH != null && Number.isFinite(startH) && Number.isFinite(endH)) {
      const win = strategyRdKillzoneWindowUtc({
        barTime: loc.barTime,
        startHour: startH,
        endHour: endH,
      });
      if (win) {
        const rng = rangeInWindow(opts.candles, win.fromIso, win.toIso);
        if (rng) {
          out.push({
            id: `${prefix}:kz`,
            label: session?.name || sid || "KZ",
            kind: "zone",
            barTime: loc.barTime,
            fromBarTime: win.fromIso,
            toBarTime: win.toIso,
            high: rng.high,
            low: rng.low,
            price: (rng.high + rng.low) / 2,
            showLabel: true,
          });
        }
      }
    }
    pushMarker(out, { id: `${prefix}:kz-hit`, label: session?.name || "KZ", barTime: loc.barTime });
  }

  // --- Oscillator / volume pane hit markers (continuous series via Lane-B) ---
  if (strategyRdWaveCIsOscillatorId(id) && loc.barTime) {
    // Dual-role *_div as confluence with full geometry already returned above.
    const isAtrRegime = id.includes("atr_regime");
    const badge =
      isAtrRegime && loc.atrRank != null
        ? `${strategyRdOscConfluenceBadge(id, loc)} ${Math.round(loc.atrRank)}th pct`
        : strategyRdOscConfluenceBadge(id, loc);
    // ATR regime is relative now: its panel plots the ATR rank (0–100), so mark the rank.
    const panePrice =
      (isAtrRegime ? loc.atrRank : null) ??
      // Trend tests the MACD line, not the histogram — mark it on the line.
      (id.includes("macd_trend") ? loc.macd : null) ??
      loc.rsi ??
      loc.macdHist ??
      loc.macd ??
      loc.stochK ??
      loc.mfi ??
      loc.adx ??
      loc.atrPct ??
      null;
    const paneId =
      loc.macdHist != null || loc.macd != null || id.includes("macd")
        ? "tessera-macd"
        : "tessera-panes";
    pushMarker(out, {
      id: `${prefix}:osc`,
      label: badge,
      barTime: loc.barTime,
      price: panePrice,
      paneId: panePrice != null ? paneId : null,
      showLabel: true,
    });
  }

  if (strategyRdWaveCIsVolumeId(id) && loc.barTime) {
    pushMarker(out, {
      id: `${prefix}:vol`,
      label: strategyRdWaveCVolumeLabel(loc, label, opts.meta),
      barTime: loc.barTime,
      price: strategyRdWaveCVolumePanePrice(loc),
      paneId: "volume",
      showLabel: true,
    });
  }

  // --- RS: minimal annotation only (no peer pane) ---
  if (strategyRdWaveCIsRsId(id) && loc.barTime) {
    const bits: string[] = [label];
    if (loc.rs != null) bits.push(`RS ${loc.rs.toFixed(2)}`);
    if (loc.mansfieldRs != null) bits.push(`M ${loc.mansfieldRs.toFixed(2)}`);
    if (loc.corr != null) bits.push(`ρ ${loc.corr.toFixed(2)}`);
    pushMarker(out, {
      id: `${prefix}:rs`,
      label: bits.join(" · "),
      barTime: loc.barTime,
      showLabel: true,
    });
  }

  return out;
}
